/**
 * Per-user Hub preferences: pinned items (Home → Quick links, the rail's Pinned)
 * and the remembered client filter.
 *
 * Pins are saved to the account (GET/PUT /home/pins, migration 040) so they follow
 * the person to any browser or device. The browser keeps a copy for an instant
 * first render; the server copy wins once loaded. The first time an account has
 * nothing saved, the browser's old pins are uploaded. The remembered client is
 * still browser-only. Every storage access is wrapped so a private window or
 * blocked storage just means "no cached pins".
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchPins, savePins } from '../services/homeApi';

const PINS_EVENT = 'ow:pins-changed';

function pinsKey(userId: string): string {
  return `ow.hubs.pins.${userId}`;
}

function clientKey(userId: string): string {
  return `ow.hubs.client.${userId}`;
}

export function readPins(userId: string): string[] {
  try {
    const raw = localStorage.getItem(pinsKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function writePins(userId: string, ids: string[]): void {
  try {
    localStorage.setItem(pinsKey(userId), JSON.stringify(ids));
  } catch {
    /* storage unavailable */
  }
  window.dispatchEvent(new Event(PINS_EVENT));
}

/** Users whose pins were loaded from the server in this page session. */
const loadedFromServer = new Set<string>();

function loadServerPins(userId: string): void {
  if (loadedFromServer.has(userId)) return;
  loadedFromServer.add(userId);
  fetchPins()
    .then((res) => {
      if (res.stored) {
        writePins(userId, res.item_ids);
        return;
      }
      // Nothing saved for this account yet: keep (and upload) this browser's pins.
      const local = readPins(userId);
      if (local.length > 0) savePins(local).catch(() => loadedFromServer.delete(userId));
    })
    .catch(() => loadedFromServer.delete(userId));
}

/** Save pins: cached locally at once, then to the account. */
export function setPins(userId: string, ids: string[]): void {
  writePins(userId, ids);
  savePins(ids).catch(() => {
    // Couldn't save: reload the account's copy so the screen shows the truth.
    loadedFromServer.delete(userId);
    loadServerPins(userId);
  });
}

export function usePinnedItems(userId: string | undefined): {
  pins: string[];
  isPinned: (id: string) => boolean;
  togglePin: (id: string) => void;
  setPinOrder: (ids: string[]) => void;
} {
  const [pins, setPinsState] = useState<string[]>(() => (userId ? readPins(userId) : []));

  useEffect(() => {
    if (!userId) return;
    const sync = () => setPinsState(readPins(userId));
    sync();
    window.addEventListener(PINS_EVENT, sync);
    window.addEventListener('storage', sync);
    loadServerPins(userId);
    return () => {
      window.removeEventListener(PINS_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [userId]);

  const isPinned = useCallback((id: string) => pins.includes(id), [pins]);
  const togglePin = useCallback(
    (id: string) => {
      if (!userId) return;
      const current = readPins(userId);
      setPins(userId, current.includes(id) ? current.filter((p) => p !== id) : [...current, id]);
    },
    [userId],
  );
  const setPinOrder = useCallback(
    (ids: string[]) => {
      if (userId) setPins(userId, ids);
    },
    [userId],
  );

  return { pins, isPinned, togglePin, setPinOrder };
}

export function readRememberedClient(userId: string): string | null {
  try {
    return localStorage.getItem(clientKey(userId));
  } catch {
    return null;
  }
}

export function rememberClient(userId: string, clientId: string): void {
  try {
    localStorage.setItem(clientKey(userId), clientId);
  } catch {
    /* storage unavailable */
  }
}
